import { useCallback, useEffect, useMemo, useState } from 'react';
import { SerializedWidget } from 'fym-common/src/db/serializersTypes';
import { useUpdateWidgetMutation } from '../../../../redux/api/fymAPI';
import { useEditorSaves } from '../saves/useEditorSaves';

/** Moves one item of a list to a new index. */
export function moveItem<T>(items: readonly T[], from: number, to: number): T[] {
  const next = [...items];
  const [item] = next.splice(from, 1);
  if (item === undefined) {
    return next;
  }
  next.splice(Math.max(0, Math.min(to, next.length)), 0, item);
  return next;
}

interface UseBlockOrderProps {
  accountId: number;
  smartLinkId: number;
  /** The rows the panel shows, in page order (a row whose delete waits is left out). */
  widgets: readonly SerializedWidget[];
}

/**
 * The order of the rows (design a7). A drop shows the new order at once and sends one PATCH of
 * `position`; the answer replaces the page and the local order goes away.
 */
export function useBlockOrder({ accountId, smartLinkId, widgets }: UseBlockOrderProps) {
  const [updateWidget] = useUpdateWidgetMutation();
  const saves = useEditorSaves();
  const [override, setOverride] = useState<string[] | null>(null);

  // A new answer from the server is the truth again.
  useEffect(() => setOverride(null), [widgets]);

  const ordered = useMemo(() => {
    if (override === null) {
      return widgets;
    }
    const byUUID = new Map(widgets.map((widget) => [widget.uuid, widget]));
    return override
      .map((uuid) => byUUID.get(uuid))
      .filter((widget): widget is SerializedWidget => widget !== undefined);
  }, [override, widgets]);

  const moveTo = useCallback(
    (widgetUUID: string, toIndex: number) => {
      const from = ordered.findIndex((widget) => widget.uuid === widgetUUID);
      const target = ordered[toIndex];
      const widget = ordered[from];
      if (from === -1 || target === undefined || widget === undefined || from === toIndex) {
        return;
      }
      setOverride(moveItem(ordered, from, toIndex).map((one) => one.uuid));
      // The block takes the place of the block that sits at the target now; the server moves the rest.
      const call = updateWidget({
        accountId,
        smartLinkId,
        widgetUUID,
        position: target.position,
        type: widget.type,
      }).unwrap();
      (saves?.track(call) ?? call).catch(() => setOverride(null));
    },
    [accountId, ordered, saves, smartLinkId, updateWidget]
  );

  const moveBy = useCallback(
    (widgetUUID: string, delta: number) => {
      const from = ordered.findIndex((widget) => widget.uuid === widgetUUID);
      if (from === -1) {
        return;
      }
      moveTo(widgetUUID, from + delta);
    },
    [moveTo, ordered]
  );

  return { ordered, moveTo, moveBy };
}
